/* eslint-disable @typescript-eslint/no-explicit-any */
import {AsyncPipe, DatePipe, NgTemplateOutlet} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, Observable, Subject} from 'rxjs';

import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {
  IMultiSelectRequest,
  IMultiSelectState,
  ISelectRequest,
  ISelectState,
} from '@app/shared/models/dynamic-form.model';
import {
  EDirection,
  EDynamicFormFieldType,
  ERestriction,
  IDynamicFormCheckbox,
  IDynamicFormFieldType,
  IDynamicFormMultiSelect,
  IDynamicFormOption,
  IDynamicFormSelect,
} from '@app/shared/models/dynamic.model';
import {selectMultiSelectState, selectSelectState} from '@app/shared/state/dynamic-form/dynamic-form.selector';
import {DynamicFormBuilderUtils} from '@app/shared/utils/dynamic-form-builder.utils';
import {DynamicFormFieldUtils} from '@app/shared/utils/dynamic-form-field.utils';
import {DynamicFormValidationUtils} from '@app/shared/utils/dynamic-form-validation.utils';
import {DynamicFormValuesUtils} from '@app/shared/utils/dynamic-form-values.utils';
import {DynamicFormUtils} from '@app/shared/utils/dynamic-form.utils';

import {Store} from '@ngrx/store';
import {TranslatePipe} from '@ngx-translate/core';
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from 'primeng/accordion';
import {Bind} from 'primeng/bind';
import {Button} from 'primeng/button';
import {Checkbox} from 'primeng/checkbox';
import {Divider} from 'primeng/divider';
import {InputNumber} from 'primeng/inputnumber';
import {InputText} from 'primeng/inputtext';
import {Message} from 'primeng/message';
import {MultiSelect as MultiSelect_1} from 'primeng/multiselect';
import {RadioButton} from 'primeng/radiobutton';
import {Ripple} from 'primeng/ripple';
import {Select} from 'primeng/select';

import {multiSelectAction, selectAction} from '@app/shared/state/dynamic-form/dynamic-form.actions';
import {MaskedCalendarComponent} from '../masked-calendar/masked-calendar.component';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    Bind,
    Message,
    Divider,
    Accordion,
    AccordionPanel,
    Ripple,
    AccordionHeader,
    AccordionContent,
    Button,
    InputText,
    InputNumber,
    MaskedCalendarComponent,
    Select,
    MultiSelect_1,
    RadioButton,
    Checkbox,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class DynamicFormComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly formStatusEvent = output<FormGroup<any>>();
  readonly formValuesEvent = output<any>();

  readonly leftLabels = input(false);
  readonly formTemplateJson = input<string>('');
  readonly formValuesJson = input<string>('');
  readonly readonly = input<boolean>(false);

  private readonly destroyRef = inject(DestroyRef);

  formTemplateList: IDynamicFormFieldType[] = [];
  dynamicForm: FormGroup = {} as FormGroup;

  eDynamicFormType = EDynamicFormFieldType;
  eRestriction = ERestriction;
  eDirection = EDirection;
  dynamicFormUtils = DynamicFormUtils;

  multiSelectState$: Observable<IMultiSelectState>;
  selectState$: Observable<ISelectState>;

  actionStatus = EActionStatus;

  accordionExpandValue: {[key: string]: any} = {};
  variableAccordionExpandValue: {[key: string]: any} = {};

  private readonly singleorMultiFilterSubject = new Subject<{
    event: any;
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect;
  }>();

  constructor() {
    this.multiSelectState$ = this.store.select(selectMultiSelectState);
    this.selectState$ = this.store.select(selectSelectState);

    effect(() => {
      const value = this.formValuesJson();

      if (!this.dynamicForm?.controls) return;

      if (value) {
        DynamicFormValuesUtils.setFormValues(value, this.formTemplateList, this.dynamicForm);
        this.cdr.markForCheck();
      } else {
        this.dynamicForm.reset();
      }
    });

    effect(() => {
      const json = this.formTemplateJson();

      if (json) {
        this.formTemplateList = JSON.parse(json);
        this.initialize();
      }
    });

    effect(() => {
      const value = this.readonly();

      if (!this.dynamicForm?.controls) return;

      if (value) {
        this.dynamicForm.disable();
      } else {
        this.dynamicForm.enable();
      }
    });
  }

  ngOnInit() {
    this.initialize();
    this.setUpsingleorMultiFilterSubject();
  }

  setUpsingleorMultiFilterSubject() {
    this.singleorMultiFilterSubject
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(({event, formTemplateItem}) => {
        if (formTemplateItem.lazy && formTemplateItem.apiEndpointUrl) {
          formTemplateItem.filterValue = event.filter;

          const request = this.getSingleOrMultiSelectPayload(formTemplateItem, 0);

          if (formTemplateItem.type === EDynamicFormFieldType.MultiSelect) {
            this.store.dispatch(
              multiSelectAction({
                request,
                formControlName: formTemplateItem.name,
              })
            );
          } else {
            this.store.dispatch(
              selectAction({
                request: request as ISelectRequest,
                formControlName: formTemplateItem.name,
              })
            );
          }
        }
      });
  }

  private initialize() {
    this.dynamicForm = this.formBuilder.group({});

    DynamicFormBuilderUtils.readonly = this.readonly();
    DynamicFormBuilderUtils.buildFormGroup(this.formTemplateList, this.dynamicForm);

    this.accordionExpandValue = DynamicFormBuilderUtils.accordionExpandValue;
    this.variableAccordionExpandValue = DynamicFormBuilderUtils.variableAccordionExpandValue;

    if (this.formValuesJson()) {
      DynamicFormValuesUtils.setFormValues(this.formValuesJson(), this.formTemplateList, this.dynamicForm);
    }

    this.setupFormSubscriptions();

    this.formStatusEvent.emit(this.dynamicForm);
    this.formValuesEvent.emit(DynamicFormValuesUtils.getFormValues(this.dynamicForm.value, this.formTemplateList));

    this.cdr.markForCheck();
  }

  private setupFormSubscriptions() {
    this.dynamicForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formStatusEvent.emit(this.dynamicForm);
      this.cdr.markForCheck();
    });

    this.dynamicForm.valueChanges
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe((value: any) => {
        DynamicFormValidationUtils.updateConditionalValidators(this.dynamicForm, this.formTemplateList);

        this.formStatusEvent.emit(this.dynamicForm);
        this.formValuesEvent.emit(DynamicFormValuesUtils.getFormValues(value, this.formTemplateList));

        this.cdr.markForCheck();
      });
  }

  getControlByName(absControl: AbstractControl, name: string): FormControl | null {
    return DynamicFormValidationUtils.getControlByName(absControl, name);
  }

  getRestriction(formTemplate: IDynamicFormFieldType, restrictionType: ERestriction) {
    return DynamicFormValidationUtils.getRestriction(formTemplate, restrictionType);
  }

  getRestrictionValue(formTemplate: IDynamicFormFieldType, restrictionType: ERestriction) {
    return DynamicFormValidationUtils.getRestrictionValue(formTemplate, restrictionType);
  }

  isRequired(formTemplate: IDynamicFormFieldType, parentGroup: FormGroup, index?: number) {
    return DynamicFormValidationUtils.isRequired(formTemplate, parentGroup, this.formTemplateList, index);
  }

  isDisabled(formTemplate: IDynamicFormFieldType, parentGroup: FormGroup, index?: number) {
    return DynamicFormValidationUtils.isDisabled(formTemplate, parentGroup, this.formTemplateList, index);
  }

  isHidden(formTemplate: IDynamicFormFieldType, parentGroup: FormGroup, index?: number) {
    return DynamicFormValidationUtils.isHidden(formTemplate, parentGroup, this.formTemplateList, index);
  }

  onCheckboxChecked(
    parentGroup: FormGroup,
    formTemplateItem: IDynamicFormCheckbox,
    currentOption: IDynamicFormOption,
    value: string[]
  ) {
    DynamicFormFieldUtils.onCheckboxChecked(parentGroup, formTemplateItem, currentOption, value);
  }

  onSubmit() {
    this.formStatusEvent.emit(this.dynamicForm);

    if (this.dynamicForm.valid) {
      this.formValuesEvent.emit(DynamicFormValuesUtils.getFormValues(this.dynamicForm.value, this.formTemplateList));
    } else {
      this.dynamicForm.markAllAsTouched();
    }
  }

  getLabelColClass(formTemplateItem: IDynamicFormFieldType) {
    return DynamicFormFieldUtils.getLabelColClass(formTemplateItem);
  }

  getFieldColClass(formTemplateItem: IDynamicFormFieldType) {
    return DynamicFormFieldUtils.getFieldColClass(formTemplateItem);
  }

  getSingleOrMultiSelectPlaceholder(
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect,
    state: IMultiSelectState | ISelectState
  ) {
    return DynamicFormFieldUtils.getSingleOrMultiSelectPlaceholder(formTemplateItem, state);
  }

  isSingleOrMultiSelectLoading(
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect,
    state: IMultiSelectState | ISelectState
  ) {
    return DynamicFormFieldUtils.isSingleOrMultiSelectLoading(formTemplateItem, state);
  }

  loadSingleOrMultiSelectOptions(
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect,
    state: IMultiSelectState | ISelectState
  ) {
    return DynamicFormFieldUtils.loadSingleOrMultiSelectOptions(formTemplateItem, state);
  }

  private getSingleOrMultiSelectPayload(
    formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect,
    pageNumber: number
  ): IMultiSelectRequest {
    return DynamicFormFieldUtils.getSingleOrMultiSelectPayload(formTemplateItem, pageNumber);
  }

  onSingleOrMultiSelectClick(event: any, formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect) {
    if (formTemplateItem.lazy && formTemplateItem.apiEndpointUrl) {
      if (formTemplateItem.type === EDynamicFormFieldType.MultiSelect) {
        this.store.dispatch(
          multiSelectAction({
            request: this.getSingleOrMultiSelectPayload(formTemplateItem, 0),
            formControlName: formTemplateItem.name,
          })
        );
      } else {
        this.store.dispatch(
          selectAction({
            request: this.getSingleOrMultiSelectPayload(formTemplateItem, 0) as ISelectRequest,
            formControlName: formTemplateItem.name,
          })
        );
      }
    }
  }

  onSingleOrMultiSelectFilter(event: any, formTemplateItem: IDynamicFormMultiSelect | IDynamicFormSelect) {
    if (formTemplateItem.filterAtClient) return;

    this.singleorMultiFilterSubject.next({event, formTemplateItem});
  }

  removeVariableAccordionTab(field: IDynamicFormFieldType, index: number) {
    DynamicFormBuilderUtils.removeVariableAccordionTab(this.dynamicForm, field, index);
    this.cdr.markForCheck();
  }

  addVariableAccordionTab(field: IDynamicFormFieldType) {
    DynamicFormBuilderUtils.addVariableAccordionTab(this.dynamicForm, field);
    this.accordionExpandValue = DynamicFormBuilderUtils.accordionExpandValue;
    this.variableAccordionExpandValue = DynamicFormBuilderUtils.variableAccordionExpandValue;
    this.cdr.markForCheck();
  }
}
